import { FC, useCallback, useMemo, useState } from 'react';
import { Box, debounce } from '@mui/material';
import InputField from 'components/InputField';
import { useLanguage } from 'context/AppContext';
import Button from 'components/Button';
import theme from 'App.theme';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material';

type SearchInputProps = {
  value?: string;
  disabled?: boolean;
  placeholder?: string;
  width?: string;
  onChange: (v: string) => void;
  sx?: SxProps<Theme>;
  inputSx?: SxProps<Theme>;
};

const SearchInput: FC<SearchInputProps> = ({ value, disabled = false, placeholder, width, onChange, sx, inputSx }) => {
  const [lang] = useLanguage();
  const [query, setQuery] = useState(value || '');

  const onChangeDebounced = useMemo(() => debounce(onChange, 500), [onChange]);

  const handleChange = useCallback(
    (value: string) => {
      if (!value) {
        onChange(value);
      }

      setQuery(value);
      onChangeDebounced(value);
    },
    [onChange, onChangeDebounced],
  );

  const handleClick = useCallback(() => {
    onChange(query);
  }, [onChange, query]);

  return (
    <Box sx={{ mr: theme.layout.spacing.small, display: 'flex', flexDirection: 'row', ...sx }}>
      <InputField
        value={query}
        type="search"
        variant="outlined"
        fullWidth
        width={width}
        placeholder={placeholder || lang.SearchInput.placeholder}
        onChange={e => handleChange(e.target.value)}
        sx={inputSx}
      />
      <Button
        label={lang.SearchInput.buttonLabel}
        colors="white on active"
        onClick={handleClick}
        disabled={disabled}
        sx={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
      />
    </Box>
  );
};

export default SearchInput;
